import { Injectable, Output } from '@angular/core';
import { Store } from '@ngxs/store';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, map, shareReplay, switchMap } from 'rxjs';
import { DistrictSimpleDto } from 'src/app/common/dtos/district.dto';
import { SchoolDTO, SchoolYearDTO } from 'src/app/common/dtos/school.dto';
import { KpiReportFilter } from 'src/app/common/dtos/reports.dto';
import {
  filtersetFiltersFromFilterDTO,
  translateKpiReportFilterFromFilterDTO,
} from 'src/app/common/dtos/translators/report-filters.translator';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import {
  UserRole,
  compareRoles,
} from 'src/app/common/state/user/role/user-role';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { UserLiteDTO } from 'src/app/common/state/user/user.dto';
import {
  FormAnswerDTO,
  ReportFiltersDTO,
} from 'src/app/private/pages/reporting-page/filters/report-filters.dto';
import {
  FilterList,
  FilterType,
} from '../../components/applied-filters/applied-filters.dto';
import {
  FiltersetValueItem,
  ReportFiltersetDTO,
} from '../../dtos/report-filterset.dto';
import { CoachingSessionTypeDTO } from '../../dtos/coaching-session.dto';
import { CompetencyDTO } from '../../dtos/competencies.dto';
import { PdEvent } from '../../dtos/pd.dto';
import { DWCohort } from '../../types/responses/cohort.responses';
import { ProFormsAPIResponse } from '../../types/responses/proforms.responses';
import { SchoolSearchService } from '../school-search/school-search.service';
import { SchoolYearService } from '../school-year/school-year.service';
import {
  CURRENT_SCHOOL_YEAR,
  getDistrictSchoolYearFromUser,
} from '../school-year/school-year.utilities';
import { ReportFiltersetService } from './report-filterset.service';
import { ReportSessionDetailsService } from './report-session-details.service';

// Dedicated slug for the transient draft filter set used to look up rubric
// topics. Kept separate from real report slugs so it never overwrites a
// report's own draft, and it is excluded from the saved-filters dropdowns.
const RUBRIC_DIMENSION_SLUG = 'rubric_dimension';

@Injectable({
  providedIn: 'root',
})
export class ReportPageFiltersService {
  // When subscribing to this, make sure to pipe(skip(1), ...)
  // This will ignore the inital value (will be empty or previous page filter)
  @Output() readonly filters: BehaviorSubject<ReportFiltersDTO>;

  currentFilters: ReportFiltersDTO;

  currentFiltersDictionary: { [key: string]: ReportFiltersDTO } = {};

  schoolYears: SchoolYearDTO[];

  district: DistrictSimpleDto;

  user: User;

  canSeeAllSchools = false;

  limitedSchools: SchoolDTO[] = [];

  viewingAllSchools = false;

  reportName: string;

  draftFiltersetIdMap: { [reportSlug: string]: number | null } = {};

  activeFiltersetIdMap: { [reportSlug: string]: number | null } = {};

  savedFiltersetsMap: { [reportSlug: string]: ReportFiltersetDTO[] } = {};

  filtersetsDriftMap: { [reportSlug: string]: boolean } = {};

  private draftDebounceTimers: {
    [reportSlug: string]: ReturnType<typeof setTimeout>;
  } = {};

  private districtTopicsCache: {
    [districtKey: string]: Observable<{ title: string; id: number }[]>;
  } = {};

  outputFilterChange(): void {
    if (!this.canSeeAllSchools && this.limitedSchools.length === 0) {
      return;
    }

    this.normalizeCurrentFilters();

    // Use the reportName embedded in currentFilters rather than this.reportName.
    // On multi-filter-bar pages, this.reportName is always the LAST bar's value;
    // currentFilters.reportName was stamped when those specific filters were set.
    const reportName = this.currentFilters.reportName || this.reportName;
    if (reportName) {
      // Snapshot BEFORE any async work so concurrent calls from multiple
      // filter bars on the same page don't overwrite each other's state.
      const snapshot = { ...this.currentFilters, reportName };
      this.currentFiltersDictionary[reportName] = snapshot;
      this.persistDraftThenEmit(reportName, snapshot);
    } else {
      this.filters.next(this.currentFilters);
    }
  }

  private normalizeCurrentFilters(): void {
    if (this.currentFilters.districtFilter?.length === 0) {
      this.currentFilters.districtFilter = [this.district];
    }

    const currentYear = this.schoolYears.find(
      (year) => year.id === CURRENT_SCHOOL_YEAR.id
    ) as SchoolYearDTO;
    if (this.currentFilters.schoolYearsFilter?.length === 0) {
      this.currentFilters.schoolYearsFilter = [currentYear];
    }

    if (!this.canSeeAllSchools) {
      if (
        !this.currentFilters.schoolFilter ||
        this.currentFilters.schoolFilter?.length === 0
      ) {
        this.currentFilters.schoolFilter = this.limitedSchools.slice();
        this.viewingAllSchools = true;
      } else if (
        this.currentFilters.schoolFilter.length === this.limitedSchools.length
      ) {
        this.viewingAllSchools = true;
      } else {
        this.viewingAllSchools = false;
      }
    } else {
      this.viewingAllSchools = false;
    }

    if (
      this.currentFilters.formAnswersFilter &&
      this.currentFilters.formAnswersFilter.length > 0
    ) {
      const selectedFormId = this.currentFilters.proFormFilter?.id;
      if (
        !selectedFormId ||
        selectedFormId !==
          (this.currentFilters.formAnswersFilter as FormAnswerDTO[])[0].formId
      ) {
        delete this.currentFilters.formAnswersFilter;
      }
    }
  }

  private persistDraftThenEmit(
    reportSlug: string,
    snapshot: ReportFiltersDTO
  ): void {
    // If multiple calls arrive synchronously (e.g. setReportName + explicit
    // getDistrictSchoolYear both firing in the same ngOnInit), cancel the
    // previous pending timer so only the last snapshot triggers a draft save.
    if (this.draftDebounceTimers[reportSlug] !== undefined) {
      clearTimeout(this.draftDebounceTimers[reportSlug]);
    }
    this.draftDebounceTimers[reportSlug] = setTimeout(() => {
      delete this.draftDebounceTimers[reportSlug];
      this.filtersetService
        .createFilterset({
          report_slug: reportSlug,
          is_draft: 1,
          filters: filtersetFiltersFromFilterDTO(snapshot),
        })
        .subscribe({
          next: (saved) => {
            this.draftFiltersetIdMap[reportSlug] = saved.id;
            if (this.activeFiltersetIdMap[reportSlug]) {
              this.filtersetsDriftMap[reportSlug] = true;
            }
            snapshot.reportFiltersetId = saved.id;
            this.filters.next(snapshot);
          },
          error: () => {
            snapshot.reportFiltersetId = undefined;
            this.filters.next(snapshot);
          },
        });
    });
  }

  loadSavedFiltersets(reportSlug: string): void {
    this.filtersetService.getFiltersets(reportSlug).subscribe({
      next: (res) => {
        this.savedFiltersetsMap[reportSlug] = res.items;
      },
      error: () => {},
    });
  }

  applyFilterset(filterset: ReportFiltersetDTO, reportSlug: string): void {
    this.filtersetService.applyFilterset(filterset.id).subscribe();

    this.filtersetService.getFilterset(filterset.id).subscribe({
      next: (expanded) => {
        this.activeFiltersetIdMap[reportSlug] = filterset.id;
        this.filtersetsDriftMap[reportSlug] = false;
        if (expanded.filters && expanded.filters.length > 0) {
          const parsed = expanded.filters.map((f) => {
            const raw = JSON.parse(f.filter_value ?? '[]') as
              | string[]
              | FiltersetValueItem[];
            if (!raw.length || typeof raw[0] === 'string') {
              // Old format: plain string array — no stored display names
              return {
                codename: f.filter_name,
                value: raw as string[],
                displayNames: [] as string[],
              };
            }
            // New format: { v, d }[]
            const items = raw as FiltersetValueItem[];
            return {
              codename: f.filter_name,
              value: items.map((i) => i.v),
              displayNames: items.map((i) => i.d),
            };
          });
          this.currentFilters = this.buildFiltersFromKpi(parsed, reportSlug);
        }
        // Emit directly with the named filterset ID — no draft round-trip needed.
        this.normalizeCurrentFilters();
        this.currentFiltersDictionary[reportSlug] = this.currentFilters;
        this.currentFilters.reportFiltersetId = filterset.id;
        this.filters.next(this.currentFilters);
      },
    });
  }

  saveNamedFilterset(name: string, reportSlug: string): void {
    this.filtersetService
      .createFilterset({
        report_slug: reportSlug,
        title: name,
        is_draft: 0,
        filters: filtersetFiltersFromFilterDTO(this.currentFilters),
      })
      .subscribe({
        next: (saved) => {
          if (!this.savedFiltersetsMap[reportSlug]) {
            this.savedFiltersetsMap[reportSlug] = [];
          }
          this.savedFiltersetsMap[reportSlug] = [
            saved,
            ...this.savedFiltersetsMap[reportSlug],
          ];
          this.activeFiltersetIdMap[reportSlug] = saved.id;
          this.filtersetsDriftMap[reportSlug] = false;
          this.currentFilters.reportFiltersetId = saved.id;
          this.filtersetService.applyFilterset(saved.id).subscribe();
        },
      });
  }

  getEffectiveFiltersetId(reportSlug: string): number | null {
    const activeId = this.activeFiltersetIdMap[reportSlug];
    const isDrifted = this.filtersetsDriftMap[reportSlug];
    if (activeId && !isDrifted) {
      return activeId;
    }
    return this.draftFiltersetIdMap[reportSlug] ?? null;
  }

  private buildFiltersFromKpi(
    filters: { codename: string; value: string[]; displayNames: string[] }[],
    reportSlug: string
  ): ReportFiltersDTO {
    const base: ReportFiltersDTO = {
      districtFilter: this.currentFilters.districtFilter,
      reportName: reportSlug,
    };

    const dn = (names: string[], i: number) => names[i] ?? '';
    const splitName = (full: string) => {
      const parts = full.trim().split(' ');
      return {
        first_name: parts[0] ?? '',
        last_name: parts.slice(1).join(' '),
      };
    };

    filters.forEach((f) => {
      const { value: vals, displayNames: names } = f;
      switch (f.codename) {
        case 'school_year':
          if (reportSlug === 'badges_earned_growth') {
            // This report uses schoolYearsFilter (multi-year) not schoolYearFilter.
            base.schoolYearsFilter = vals.map(
              (id, i) =>
                this.schoolYears?.find((sy) => sy.id === Number(id)) ??
                ({ id: Number(id), name: dn(names, i) } as SchoolYearDTO)
            );
          } else {
            base.schoolYearFilter =
              this.schoolYears?.find((sy) => sy.id === Number(vals[0])) ??
              (names[0]
                ? ({ id: Number(vals[0]), name: names[0] } as SchoolYearDTO)
                : undefined);
          }
          break;

        case 'district':
          base.districtFilter = vals.map((id, i) => {
            const numId = Number(id);
            if (numId === this.district?.id) return this.district;
            return {
              ...this.district,
              id: numId,
              title: dn(names, i) || this.district?.title,
            };
          });
          break;

        case 'school':
          base.schoolFilter = vals.map(
            (id, i) =>
              ({
                id: Number(id),
                title:
                  dn(names, i) ||
                  (this.limitedSchools.find((s) => s.id === Number(id))
                    ?.title ??
                    ''),
              } as SchoolDTO)
          );
          break;

        case 'cohort':
          base.cohortFilter = vals.map(
            (id, i) =>
              ({
                id: Number(id),
                name: dn(names, i),
                district_id: 0,
              } as DWCohort)
          );
          break;

        case 'session_type':
          base.logTypeFilter = vals.map(
            (id, i) =>
              ({
                id: Number(id),
                title: dn(names, i),
              } as unknown as CoachingSessionTypeDTO)
          );
          break;

        case 'assigned_coach':
          base.assignedCoachFilter = {
            id: Number(vals[0]),
            profile: splitName(dn(names, 0)),
          } as unknown as UserLiteDTO;
          break;

        case 'user':
          base.coacheesFilter = vals.map((id, i) => ({
            id: Number(id),
            profile: splitName(dn(names, i)),
          })) as unknown as UserLiteDTO[];
          break;

        case 'daterange':
          if (vals.length >= 2) {
            const pd = (s: string) => {
              const [y, m, d] = s.split('-').map(Number);
              return new NgbDate(y, m, d);
            };
            base.dateRangeFilter = [pd(vals[0]), pd(vals[1])];
          }
          break;

        case 'pd_session_type':
          base.typesFilter = vals;
          break;
        case 'category':
          base.categories = vals;
          break;
        case 'category_option':
          base.category_options = vals;
          break;
        case 'keyword':
          base.keyword = vals[0];
          break;
        case 'is_virtual':
          base.virtualFilter = Number(vals[0]);
          break;
        case 'pd_session_completed':
          base.completedFilter = Number(vals[0]);
          break;
        case 'has_option':
          base.hasOption = vals[0] === '1';
          break;
        case 'is_reviewed':
          base.isReviewed = vals[0] === '1';
          break;

        case 'rubric':
          base.topicFilter = {
            id: Number(vals[0]),
            title: dn(names, 0),
          };
          break;
        case 'form':
          base.proFormFilter = {
            id: Number(vals[0]),
            title: dn(names, 0),
          } as unknown as ProFormsAPIResponse;
          break;
        case 'competency':
          base.competencyList = vals.map((id, i) => ({
            id: Number(id),
            title: dn(names, i),
          })) as unknown as CompetencyDTO[];
          break;
        case 'pd_event':
          base.eventFilter = vals.map((id, i) => ({
            id: Number(id),
            title: dn(names, i),
          })) as unknown as PdEvent[];
          break;
        case 'pd_facilitator':
          base.facilitators = vals.map((id, i) => ({
            id: Number(id),
            profile: splitName(dn(names, i)),
          })) as unknown as UserLiteDTO[];
          break;
        case 'session_owner':
          base.sessionOwners = vals.map((id, i) => ({
            id: Number(id),
            profile: splitName(dn(names, i)),
          })) as unknown as UserLiteDTO[];
          break;

        case 'question_answer_submitted':
          base.formAnswersFilter = vals.map(
            (id, i) =>
              ({
                id: Number(id),
                title: dn(names, i),
                question: '',
                questionId: 0,
                value: '',
                formId: 0, // resolved below after the loop
              } as FormAnswerDTO)
          );
          break;

        default:
          break;
      }
    });

    // formId must match proFormFilter.id for normalizeCurrentFilters to keep the
    // form answer filter. Resolve it here in case the two codenames arrive in
    // any order within the stored filter array.
    if (base.formAnswersFilter?.length && base.proFormFilter) {
      const formId = (base.proFormFilter as ProFormsAPIResponse).id;
      (base.formAnswersFilter as FormAnswerDTO[]).forEach((a) => {
        a.formId = formId;
      });
    }

    return base;
  }

  // Updates currentFilters.proFormFilter with the full form object (including
  // sections/questions) and re-emits so filter-bar/modal subscribers pick up
  // the enriched data before the user opens the modal. Does not go through
  // outputFilterChange, so no draft is created and the named filterset is not
  // marked as drifted.
  enrichProFormFilter(form: ProFormsAPIResponse): void {
    if (this.currentFilters.proFormFilter?.id === form.id) {
      this.currentFilters.proFormFilter = form;
      this.filters.next(this.currentFilters);
    }
  }

  constructor(
    private store: Store,
    private schoolSearch: SchoolSearchService,
    private schoolYearService: SchoolYearService,
    private apiCoreService: APICoreService,
    private reportLogDetailsService: ReportSessionDetailsService,
    private filtersetService: ReportFiltersetService
  ) {
    this.filters = new BehaviorSubject<ReportFiltersDTO>(
      {} as ReportFiltersDTO
    );

    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    this.canSeeAllSchools = compareRoles(
      [UserRole.DISTRICT_ADMIN, UserRole.E2L_EMPLOYEE],
      this.user.roles
    );

    this.district = this.user.district as DistrictSimpleDto;
    this.schoolYearService.schoolYears.subscribe((schoolYears) => {
      this.schoolYears = schoolYears;
    });
  }

  setReportName(reportName: string) {
    if (this.reportName === reportName) {
      return;
    }
    this.reportName = reportName;
    if (!this.currentFiltersDictionary[reportName]) {
      // First load for given page
      this.getDistrictSchoolYear();
      this.getCoachSchools();
      this.currentFiltersDictionary[reportName] = this.currentFilters;
    } else {
      this.currentFilters = this.currentFiltersDictionary[reportName];
      const activeId = this.activeFiltersetIdMap[reportName];
      if (activeId) {
        // Named filterset is active — emit directly without a draft round-trip
        // to avoid incorrectly marking it as drifted on return navigation.
        // Use getEffectiveFiltersetId so a drifted state emits the draft ID,
        // letting report data fetching use the modified filters instead of the
        // original saved filterset.
        this.currentFilters.reportFiltersetId =
          this.getEffectiveFiltersetId(reportName) ?? undefined;
        this.filters.next(this.currentFilters);
      } else {
        const draftId = this.draftFiltersetIdMap[reportName];
        if (draftId) {
          // Draft exists from a prior visit — emit directly to avoid the async
          // round-trip that would cause the report to fetch without a filterset ID.
          this.currentFilters.reportFiltersetId = draftId;
          this.filters.next(this.currentFilters);
        } else {
          this.outputFilterChange();
        }
      }
    }
  }

  setSelectedProForm(selectedProForm: ProFormsAPIResponse): void {
    this.currentFilters.proFormFilter = selectedProForm;
    this.outputFilterChange();
  }

  getDistrictTopics(
    districtIds: number[]
  ): Observable<{ title: string; id: number }[]> {
    const key = [...districtIds].sort().join(',');
    if (!this.districtTopicsCache[key]) {
      // Persist the selected districts as a saved filter set and reference it by
      // id, mirroring how each report loads. Passing every district id inline
      // blows past the URL length limit when filtering by all districts.
      const districtFilterValue = JSON.stringify(
        districtIds.map((id) => ({ v: id.toString(), d: '' }))
      );
      this.districtTopicsCache[key] = this.filtersetService
        .createFilterset({
          report_slug: RUBRIC_DIMENSION_SLUG,
          is_draft: 1,
          filters: [
            { filter_name: 'district', filter_value: districtFilterValue },
          ],
        })
        .pipe(
          switchMap((saved) =>
            this.apiCoreService.getRequest('reporting/dimension', {
              dimensionName: 'rubric',
              report_filterset_id: saved.id,
            })
          ),
          map((res) =>
            res.items.map((rubric: { id: string; name: string }) => ({
              id: rubric.id,
              title: rubric.name,
            }))
          ),
          shareReplay(1)
        );
    }
    return this.districtTopicsCache[key];
  }

  getDistrictSchoolYear(): void {
    // Don't wipe the saved filterset state on navigation return or re-init.
    if (
      this.reportName &&
      (this.activeFiltersetIdMap[this.reportName] ||
        this.draftFiltersetIdMap[this.reportName])
    ) {
      return;
    }
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    this.currentFilters = { districtFilter: [this.district] };

    const schoolYear = getDistrictSchoolYearFromUser(this.user);
    if (this.reportName === 'badges_earned_growth') {
      this.currentFilters.schoolYearsFilter = [schoolYear];
    } else {
      this.currentFilters.schoolYearFilter = schoolYear;
    }

    this.currentFilters.reportName = this.reportName;

    if (this.canSeeAllSchools || this.limitedSchools.length > 0) {
      this.outputFilterChange();
    }
  }

  getCoachSchools() {
    if (this.canSeeAllSchools) {
      return;
    }

    if (this.limitedSchools.length === 0) {
      this.schoolSearch
        .getAssignedSchools(this.user.id)
        .subscribe((schools) => {
          if (schools.length === 0 && this.user.school) {
            this.limitedSchools = [this.user.school];
          } else {
            this.limitedSchools = [this.user.school, ...schools];
          }
          this.currentFilters.schoolFilter = this.limitedSchools.slice();
          this.viewingAllSchools = true;
          this.currentFilters.allAssignedSchools = 1;
          this.outputFilterChange();
        });
    }
  }

  removeFilter(filter: keyof ReportFiltersDTO): void {
    if (
      this.currentFilters[filter] === undefined ||
      this.currentFilters[filter] === null
    )
      return;
    delete this.currentFilters[filter];
    this.outputFilterChange();
  }

  removeFilterFromList(filterList: FilterList) {
    let useIndex = 0;
    if (filterList.index !== undefined) {
      useIndex = filterList.index;
    }
    switch (filterList.keyName) {
      case 'schoolFilter':
        if (this.currentFilters.schoolFilter) {
          this.currentFilters.schoolFilter =
            this.currentFilters.schoolFilter.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'schoolYearsFilter':
        if (this.currentFilters.schoolYearsFilter) {
          this.currentFilters.schoolYearsFilter =
            this.currentFilters.schoolYearsFilter.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'districtFilter':
        if (this.currentFilters.districtFilter) {
          this.currentFilters.districtFilter =
            this.currentFilters.districtFilter.filter(
              (item) => item !== filterList.value
            );
          this.handleDistrictRemoval(filterList.value);
          this.outputFilterChange();
        }
        break;
      case 'formAnswersFilter':
        if (this.currentFilters.formAnswersFilter) {
          this.currentFilters.formAnswersFilter =
            this.currentFilters.formAnswersFilter.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'logTypeFilter':
        if (this.currentFilters.logTypeFilter) {
          this.currentFilters.logTypeFilter =
            this.currentFilters.logTypeFilter.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'typesFilter':
        if (this.currentFilters.typesFilter) {
          this.currentFilters.typesFilter =
            this.currentFilters.typesFilter.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'keyword':
        this.currentFilters.keyword = undefined;
        this.outputFilterChange();
        break;
      case 'completedFilter':
        this.currentFilters.completedFilter = undefined;
        this.outputFilterChange();
        break;
      case 'virtualFilter':
        this.currentFilters.virtualFilter = undefined;
        this.outputFilterChange();
        break;
      case 'categories':
        if (
          this.currentFilters.categories &&
          this.currentFilters.category_options
        ) {
          this.currentFilters.categories =
            this.currentFilters.categories.filter(
              (item) => item !== filterList.value
            );
          let t = 0;
          this.currentFilters.category_options =
            this.currentFilters.category_options.filter(() => {
              const retVal = t !== useIndex;
              t += 1;
              return retVal;
            });
        }
        this.outputFilterChange();
        break;
      case 'competencyList':
        if (this.currentFilters.competencyList) {
          this.currentFilters.competencyList =
            this.currentFilters.competencyList.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'sessionOwnerList':
        if (this.currentFilters.sessionOwners) {
          this.currentFilters.sessionOwners =
            this.currentFilters.sessionOwners.filter(
              (item) => item !== filterList.value
            );
        }
        this.outputFilterChange();
        break;
      case 'facilitatorList':
        if (this.currentFilters.facilitators) {
          this.currentFilters.facilitators =
            this.currentFilters.facilitators.filter(
              (item) => item !== filterList.value
            );
        }
        this.outputFilterChange();
        break;
      case 'participantList':
        if (this.currentFilters.participants) {
          this.currentFilters.participants =
            this.currentFilters.participants.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'eventList':
        if (this.currentFilters.eventFilter) {
          this.currentFilters.eventFilter =
            this.currentFilters.eventFilter.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'coacheesFilter':
        if (this.currentFilters.coacheesFilter) {
          this.currentFilters.coacheesFilter =
            this.currentFilters.coacheesFilter.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'planUserFilter':
        if (this.currentFilters.planUserFilter) {
          this.currentFilters.planUserFilter =
            this.currentFilters.planUserFilter.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      case 'cohortFilter':
        if (this.currentFilters.cohortFilter) {
          this.currentFilters.cohortFilter =
            this.currentFilters.cohortFilter.filter(
              (item) => item !== filterList.value
            );
          this.outputFilterChange();
        }
        break;
      default:
        break;
    }
  }

  handleDistrictRemoval(filter: FilterType) {
    const district = filter as DistrictSimpleDto;
    this.currentFilters.schoolFilter = this.currentFilters.schoolFilter?.filter(
      (school) => school.district_id !== district.id
    );
  }

  queueReportDownload(
    reportName: string,
    extraFilters: KpiReportFilter[] = []
  ) {
    let report = { title: '', report: '' };
    let downloadFilters = {} as ReportFiltersDTO;
    let filtersetSlug: string | null = null;

    switch (reportName) {
      case 'gi_growth':
        report = { title: 'GI Growth', report: 'giGrowth' };
        downloadFilters = this.currentFiltersDictionary[reportName];
        filtersetSlug = 'growth';
        break;
      case 'session_details':
        report = { title: 'Session Details', report: 'logDetails' };
        downloadFilters = this.currentFiltersDictionary[reportName];
        filtersetSlug = 'sessions';
        break;
      case 'observation_reports':
        report = {
          title: 'Observation Forms Download',
          report: 'observationFormsDownload',
        };
        downloadFilters = this.currentFiltersDictionary[reportName];
        filtersetSlug = 'evidence';
        break;
      case 'proforms_reports':
        report = {
          title: 'Proform Forms Download',
          report: 'proformFormsDownload',
        };
        downloadFilters = this.currentFiltersDictionary[reportName];
        filtersetSlug = 'proforms_reports';
        break;
      case 'badges_earned_raw':
        report = { title: 'Raw Badge Data', report: 'badgeDataDownloadRaw' };
        downloadFilters = this.currentFiltersDictionary['badges_earned_growth'];
        filtersetSlug = 'growth';
        break;
      case 'microcredential_badge_summary':
        report = {
          title: 'Microcredential Badge Summary',
          report: 'badgeDataDownloadCompetency',
        };
        downloadFilters = this.currentFiltersDictionary['badges_earned_growth'];
        filtersetSlug = 'growth';
        break;
      case 'strand_badge_summary':
        report = {
          title: 'Strand Badge Summary',
          report: 'badgeDataDownloadStrand',
        };
        downloadFilters = this.currentFiltersDictionary['badges_earned_growth'];
        filtersetSlug = 'growth';
        break;
      case 'implementationPlan':
        report = {
          title: 'Implementation Plan Summary',
          report: 'implementationPlan',
        };
        downloadFilters = this.currentFiltersDictionary[reportName];
        filtersetSlug = 'plans';
        break;
      default:
        return;
    }

    const filtersetId = filtersetSlug
      ? this.getEffectiveFiltersetId(filtersetSlug)
      : null;

    const baseFilters = translateKpiReportFilterFromFilterDTO(downloadFilters);
    const requestFilters = filtersetId
      ? extraFilters
      : [...baseFilters, ...extraFilters];

    this.reportLogDetailsService
      .queueDownloadReport(requestFilters, report, filtersetId)
      .subscribe({
        complete: () => {},
        error: () => {
          // eslint-disable-next-line no-console
          console.log('search returned 0 results');
        },
      });
  }
}
